import {
  CodingSummaryCard,
  DataStatusNote,
  formatClassSection,
  formatDate,
  formatDuration,
  formatPercent,
  galleryItemsToMedia,
  GalleryMedia,
  GIL,
  GIL_CELL,
  GilColumn,
  GilGallery,
  GilReportTable,
  LearningOutcomesCard,
  NeedsAttentionCard,
  OverviewSection,
  parseIdParam,
  performanceLabel,
  ProgressMetricsCard,
  projectLabel,
  ReportLayout,
  ReportTabs,
  SectionCard,
  StatTile,
  StatusChip,
  toPercent,
  useCommonServiceClientContext,
  useGilReport,
} from '@geneo2-web/shared-ui';
import { useOpenAttentionItem } from './useOpenAttentionItem';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { Box, Typography } from '@mui/material';
import { GilProjectGroupRow } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  GIL_Reports_Group,
  View_Project,
} from '../../../routeHandling/RoutesNomenclature';

type ProjectTab = 'members' | 'evidence';

const TABS = [
  { key: 'members', label: 'Group Members', icon: <PeopleOutlineIcon /> },
  { key: 'evidence', label: 'Evidence', icon: <ImageOutlinedIcon /> },
];

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/** "17/20", or "—" while no member of the group has been marked. */
const groupScore = (group: GilProjectGroupRow) =>
  group.gradedStudentCount
    ? `${Math.round((group.groupAverageMarks ?? 0) * 100) / 100}/${
        group.maxMarks ?? 0
      }`
    : '—';

/** Dashboard 4 - one project, every group working on it. */
export const GilProjectReport = () => {
  const navigate = useNavigate();
  const openAttentionItem = useOpenAttentionItem();
  const { assessment_id } = useParams();
  const assessmentId = parseIdParam(assessment_id);
  const { LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler } =
    useCommonServiceClientContext();
  const [tab, setTab] = useState<ProjectTab>('members');

  const { state, reload } = useGilReport(
    () =>
      assessmentId
        ? LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getGilTeacherProjectReport(
            { assessmentId }
          ).then((response) => response.data)
        : undefined,
    [assessmentId]
  );

  const report = state.status === 'success' ? state.data : undefined;
  const overview = report?.overview;
  const isCollaborative = !!report?.isCollaborative;
  const groups = report?.groups ?? [];
  const groupCount = overview?.groupCount ?? 0;
  const studentCount = overview?.studentCount ?? 0;
  const completedCount = overview?.completedGroupCount ?? 0;
  //~ Evidence stays with the group that uploaded it - never pooled for the class.
  const evidenceByGroup = useMemo(() => {
    const byGroup = new Map<bigint, GalleryMedia[]>();
    (report?.gallery ?? []).forEach((item) => {
      const groupId = item.studentGroupId ?? BigInt(0);
      byGroup.set(groupId, [
        ...(byGroup.get(groupId) ?? []),
        ...galleryItemsToMedia([item], false),
      ]);
    });
    return byGroup;
  }, [report?.gallery]);

  const openGroup = (group: GilProjectGroupRow) =>
    navigate(`${GIL_Reports_Group}/${assessmentId}/${group.studentGroupId}`);

  const indexColumn: GilColumn<GilProjectGroupRow> = {
    key: 'index',
    header: '#',
    align: 'left',
    render: (_group, index) => (
      <Typography sx={GIL_CELL.index}>{index + 1}</Typography>
    ),
  };
  //~ The underlined name is the drill-down into the group report.
  const groupColumn: GilColumn<GilProjectGroupRow> = {
    key: 'group',
    header: 'Group Name',
    align: 'left',
    minWidth: 180,
    render: (group) => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          minWidth: 0,
        }}
      >
        <Typography
          noWrap
          role="link"
          tabIndex={0}
          onClick={() => openGroup(group)}
          onKeyDown={(event) => event.key === 'Enter' && openGroup(group)}
          sx={GIL_CELL.link}
        >
          {group.groupName || 'Whole class'}
        </Typography>
        <Typography noWrap sx={GIL_CELL.sub}>
          {[
            plural(group.studentCount, 'Student'),
            group.lastActivityAt
              ? `last worked ${formatDate(group.lastActivityAt)}`
              : undefined,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Box>
    ),
  };
  const statusColumn: GilColumn<GilProjectGroupRow> = {
    key: 'status',
    header: 'Status',
    render: (group) => <StatusChip status={group.status} />,
  };

  const memberColumns: GilColumn<GilProjectGroupRow>[] = [
    indexColumn,
    groupColumn,
    {
      key: 'time',
      header: isCollaborative ? 'Group Time' : 'Total Time',
      render: (group) => formatDuration(group.timeSpent?.totalTimeInSec),
    },
    {
      key: 'score',
      header: 'Project Score',
      render: (group) => (
        <Typography noWrap sx={GIL_CELL.strong}>
          {groupScore(group)}
        </Typography>
      ),
    },
    statusColumn,
  ];

  return (
    <ReportLayout
      showImage
      title={report ? projectLabel(report.project) : 'Project Report'}
      subtitle={
        report
          ? [
              `Class ${formatClassSection(report.classSection)}`,
              report.teacherName,
            ]
              .filter(Boolean)
              .join(' | ')
          : undefined
      }
      meta={
        report && [
          {
            ...(isCollaborative
              ? { label: 'Groups', value: groupCount }
              : { label: 'Group', value: 'Whole class' }),
            color: '#FFF8E6',
            textColor: GIL.muted,
          },
          {
            label: 'Students',
            value: studentCount,
            iconName: 'user',
            color: '#EEF0FF',
            textColor: '#2E3192',
          },
        ]
      }
      state={state}
      onRetry={reload}
    >
      <DataStatusNote status={report?.dataStatus} />

      <OverviewSection
        title="Group Overview"
        subtitle="Quick summary of group activity and performance"
        action={{
          label: 'View Project',
          onClick: () => navigate(`${View_Project}/${assessmentId}`),
        }}
      >
        <StatTile
          tone="purple"
          icon={<GroupsOutlinedIcon />}
          value={groupCount}
          label={groupCount === 1 ? 'Group' : 'Groups'}
        />
        <StatTile
          tone="green"
          icon="gil-group-green"
          value={studentCount}
          label={studentCount === 1 ? 'Student' : 'Students'}
        />
        <StatTile
          tone="blue"
          icon="gil-submitted-blue"
          value={`${completedCount}/${groupCount}`}
          label="Submitted"
          caption={`${Math.round(
            toPercent(completedCount, groupCount)
          )}% submission rate`}
        />
        <StatTile
          tone="yellow"
          icon={<AccessTimeIcon />}
          //~ Per group: the group shares one lab device and login.
          value={formatDuration(overview?.avgTimePerGroupInSec)}
          label="Avg. Time"
          caption={isCollaborative ? 'Per Group' : 'Per Student'}
        />
        <StatTile
          tone="red"
          icon="gil-star-red"
          value={formatPercent(overview?.avgProjectScorePercent)}
          label="Avg. Project Score"
          caption={performanceLabel(overview?.avgProjectScorePercent)}
        />
      </OverviewSection>

      <NeedsAttentionCard
        items={report?.needsAttention}
        onOpen={openAttentionItem}
        showProject={false}
      />

      <ProgressMetricsCard metrics={overview?.progressMetrics} />

      <ReportTabs
        tabs={TABS}
        value={tab}
        onChange={(key) => setTab(key as ProjectTab)}
      />

      {tab === 'members' && (
        <>
          <SectionCard
            plain
            title="Group Members & Performance"
            subtitle="Detailed performance of each group in this project"
          >
            <GilReportTable
              columns={memberColumns}
              rows={groups}
              rowKey={(group) => String(group.studentGroupId)}
              emptyText="No groups found for this project"
            />
          </SectionCard>
          <LearningOutcomesCard
            outcomes={report?.learningOutcomes}
            subtitle="How the class is doing on each learning outcome of this project"
          />
          <CodingSummaryCard summary={overview?.codingSummary} />
        </>
      )}

      {tab === 'evidence' &&
        (isCollaborative ? (
          groups.map((group) => (
            <SectionCard
              key={String(group.studentGroupId)}
              plain
              title={group.groupName || 'Group'}
              subtitle={plural(group.studentCount, 'Student')}
            >
              <GilGallery
                items={evidenceByGroup.get(group.studentGroupId) ?? []}
                title={group.groupName}
              />
            </SectionCard>
          ))
        ) : (
          <SectionCard plain title="Student Uploads" subtitle="Whole class">
            <GilGallery items={evidenceByGroup.get(BigInt(0)) ?? []} />
          </SectionCard>
        ))}
    </ReportLayout>
  );
};

export default GilProjectReport;
